import { http } from '@/api/http'
import {
  pagedPlayersSchema,
  pagedTransactionsSchema,
  waiverClaimSchema,
  waiverClaimListSchema,
  addDropResultSchema,
  tradeSchema,
  tradeListSchema,
  type PlayerPosition,
  type WaiverStatus,
  type TradeStatus,
} from '@/types/domain'

export interface FreeAgentsQuery {
  search?: string
  position?: PlayerPosition
  page?: number
  pageSize?: number
}

export interface AddDropPayload {
  addPlayerId: string
  // Omitted when the roster has an open slot; required when dropping to make room.
  dropPlayerId?: string
}

export interface SubmitWaiverPayload {
  teamId: string
  addPlayerId: string
  dropPlayerId?: string
}

export interface ProposeTradePayload {
  proposingTeamId: string
  receivingTeamId: string
  offeredPlayerIds: string[]
  requestedPlayerIds: string[]
}

function base(leagueId: string): string {
  return `/api/leagues/${encodeURIComponent(leagueId)}`
}

function freeAgentsQuery(query: FreeAgentsQuery): string {
  const params = new URLSearchParams()
  if (query.search) params.set('search', query.search)
  if (query.position) params.set('position', query.position)
  if (query.page != null) params.set('page', String(query.page))
  if (query.pageSize != null) params.set('pageSize', String(query.pageSize))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** `?teamId=&status=` filter shared by the waiver/trade list endpoints. */
function listQuery(filter: { teamId?: string; status?: string }): string {
  const params = new URLSearchParams()
  if (filter.teamId) params.set('teamId', filter.teamId)
  if (filter.status) params.set('status', filter.status)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function pageQuery(page?: number, pageSize?: number): string {
  const params = new URLSearchParams()
  if (page != null) params.set('page', String(page))
  if (pageSize != null) params.set('pageSize', String(pageSize))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Free agents, add/drop, waivers, trades & transaction-log endpoints (see
 * M4-WAIVERS-TRADES-CONTRACT §REST endpoints). Types are inferred from the zod schemas; the backend
 * owns all transaction/roster rules. Mutations carry the antiforgery header via `http`.
 */
export const transactionsApi = {
  // --- Free agents / add-drop -------------------------------------------------------------

  getFreeAgents: (leagueId: string, query: FreeAgentsQuery = {}) =>
    http.get(`${base(leagueId)}/free-agents${freeAgentsQuery(query)}`, pagedPlayersSchema),

  addDrop: (leagueId: string, teamId: string, payload: AddDropPayload) =>
    http.post(
      `${base(leagueId)}/teams/${encodeURIComponent(teamId)}/add-drop`,
      payload,
      addDropResultSchema,
    ),

  // --- Waivers ----------------------------------------------------------------------------

  submitWaiver: (leagueId: string, payload: SubmitWaiverPayload) =>
    http.post(`${base(leagueId)}/waivers`, payload, waiverClaimSchema),

  listWaivers: (leagueId: string, filter: { teamId?: string; status?: WaiverStatus } = {}) =>
    http.get(`${base(leagueId)}/waivers${listQuery(filter)}`, waiverClaimListSchema),

  cancelWaiver: (leagueId: string, claimId: string) =>
    http.del<void>(`${base(leagueId)}/waivers/${encodeURIComponent(claimId)}`),

  // --- Trades -----------------------------------------------------------------------------

  proposeTrade: (leagueId: string, payload: ProposeTradePayload) =>
    http.post(`${base(leagueId)}/trades`, payload, tradeSchema),

  listTrades: (leagueId: string, filter: { teamId?: string; status?: TradeStatus } = {}) =>
    http.get(`${base(leagueId)}/trades${listQuery(filter)}`, tradeListSchema),

  acceptTrade: (leagueId: string, tradeId: string) =>
    http.post(`${base(leagueId)}/trades/${encodeURIComponent(tradeId)}/accept`, undefined, tradeSchema),

  rejectTrade: (leagueId: string, tradeId: string) =>
    http.post(`${base(leagueId)}/trades/${encodeURIComponent(tradeId)}/reject`, undefined, tradeSchema),

  cancelTrade: (leagueId: string, tradeId: string) =>
    http.post(`${base(leagueId)}/trades/${encodeURIComponent(tradeId)}/cancel`, undefined, tradeSchema),

  // --- Transaction log --------------------------------------------------------------------

  listTransactions: (leagueId: string, page?: number, pageSize?: number) =>
    http.get(`${base(leagueId)}/transactions${pageQuery(page, pageSize)}`, pagedTransactionsSchema),
}
